import { Locale } from 'vue-i18n'
import { ResultOrError } from '@lukso/controllerapps-sharedlogic-ddd-core'

import {
  Approval,
  ApprovalApi,
  APPROVAL_ACTION,
  ApprovalOutput,
} from '@/shared/apis/approvals'
import { Profile, UpAddress, EoaAddress } from '@/shared/models/Profile'
import {
  TransactionIntermediateData,
  TransactionOptions,
} from '@/shared/models/Transaction'
import { PopupNetworkInfo } from '@/shared/models/Network'
import { Identicon } from '@/shared/models/Identicon'
import {
  BackUpProfilesUseCaseRequestPropsDTO,
  CheckBackupFileUseCaseRequestPropsDTO,
  RestoreProfilesUseCaseRequestPropsDTO,
  CheckBackupFileUseCaseResponseDTO,
  UnlockKeyringUseCaseResponseDTO,
  ChangePasswordUseCasePropsDTO,
} from '@/business-logic/wallet'
import { ControllerDTO } from '@/business-logic/profile'
import { RelayServiceErrorDTO } from '@/business-logic/relay-service'
import { ApprovalDirective } from '@/popup/store/ApprovalsStore'
import {
  NetworkInfoDBSchema,
  RelayServiceDBSchema,
} from '@/shared/Database/UPSettings/models'

export type GetProfileData = {
  selectedProfile?: UpAddress
  selectedNetwork?: PopupNetworkInfo
  approvals: Approval<keyof ApprovalApi, unknown>[]
}

export type InternalAPI = {
  [InternalMethods.HANDLE_APPROVAL]: {
    payload: {
      action: APPROVAL_ACTION
      requestId: string
      data?: ApprovalOutput<any> | { errorMessage?: string }
    }
    response: ApprovalDirective | undefined
  }
  [InternalMethods.UNLOCK]: {
    payload: { password: string }
    response: UnlockKeyringUseCaseResponseDTO
  }
  [InternalMethods.VALIDATE_PASSWORD]: {
    payload: { password: string }
    response: boolean
  }
  [InternalMethods.KEEP_ALIVE]: {
    payload?: undefined
    response: void
  }
  [InternalMethods.GET_CORE_STATE]: {
    payload: undefined
    response: {
      isUnlocked: boolean
      isCreated: boolean
      network: PopupNetworkInfo
      selectedProfile?: UpAddress
      appId: string | undefined
      locale: Locale
      approvals: Approval<keyof ApprovalApi, unknown>[]
    }
  }
  [InternalMethods.GET_STATE]: {
    payload: undefined
    response: GetProfileData
  }
  [InternalMethods.SWITCH_PROFILE]: {
    payload: { address: UpAddress }
    response: GetProfileData
  }
  [InternalMethods.UPLOAD_TO_IPFS]: {
    payload: Profile
    response: { data: string }
  }
  [InternalMethods.REMOVE_AUTHENTICATED_APP]: {
    payload: { origin: string }
    response: void
  }
  [InternalMethods.IMPORT_PROFILE]: {
    payload: {
      address: UpAddress
      importRequestId?: string
      lsp23ControllerAddress?: EoaAddress
    }
    response: void
  }
  [InternalMethods.CANCEL_IMPORT_PROFILE]: {
    payload: { address: UpAddress; importRequestId?: string }
    response: void
  }
  [InternalMethods.GET_CONTROLLERS]: {
    payload: { profileAddress: string }
    response: ControllerDTO[]
  }
  [InternalMethods.GET_UP_BROWSER_EXTENSION_CONTROLLER]: {
    payload: { profileAddress: string }
    response: ControllerDTO | undefined
  }
  [InternalMethods.GET_CONTROLLER_BALANCE]: {
    payload: { controllerAddress: string }
    response: string
  }
  [InternalMethods.SAVE_CUSTOM_CONTROLLER_NAME]: {
    payload: { controllerAddress: string; profileAddress: string; name: string }
    response: void
  }
  [InternalMethods.SWITCH_NETWORK]: {
    payload: { networkId: string; approvalId?: string }
    response: void
  }
  [InternalMethods.ADD_NETWORK]: {
    payload: { approvalId: string; chainId?: string }
    response: void
  }
  [InternalMethods.SWITCH_LOCALE]: {
    payload: { locale: Locale }
    response: void
  }
  [InternalMethods.REVEAL_PRIVATE_KEY]: {
    payload: { controllerAddress: string; password: string }
    response: string
  }
  [InternalMethods.RECALCULATE_TRANSACTION_DATA]: {
    payload: { requestId: string; options?: TransactionOptions }
    response: TransactionIntermediateData
  }
  [InternalMethods.FETCH_PROFILE_EVENTS]: {
    payload: { address: string }
    response: void
  }
  [InternalMethods.MINIMIZE_TRANSACTION]: {
    payload: { windowId: number }
    response: void
  }
  [InternalMethods.BACK_UP_PROFILES]: {
    payload: BackUpProfilesUseCaseRequestPropsDTO
    response: string
  }
  [InternalMethods.CHECK_BACKUP_FILE]: {
    payload: CheckBackupFileUseCaseRequestPropsDTO
    response: CheckBackupFileUseCaseResponseDTO
  }
  [InternalMethods.RESTORE_PROFILES]: {
    payload: RestoreProfilesUseCaseRequestPropsDTO
    response: void
  }
  [InternalMethods.CHANGE_PASSWORD]: {
    payload: ChangePasswordUseCasePropsDTO
    response: void
  }
  [InternalMethods.CREATE_CONTROLLER]: {
    payload: undefined
    response: string
  }
  [InternalMethods.CONFIRM_LSP23_ADDRESS]: {
    payload: {
      identicon?: Identicon
      requestId?: string
      controllerAddress?: string
    }
    response: void | Error
  }
  [InternalMethods.REMOVE_PROFILE]: {
    payload: {
      address: string
    }
    response: void
  }
  [InternalMethods.GET_NETWORKS]: {
    payload: void
    response: NetworkInfoDBSchema[]
  }
  [InternalMethods.READ_APPLICATION_ERRORS]: {
    payload: undefined
    response: void
  }
  [InternalMethods.ADD_SOMETHING_WENT_WRONG_ERROR]: {
    payload: { message: string }
    response: void
  }
  [InternalMethods.ADD_RELAY_SERVICE]: {
    payload: {
      name: string
      apiURL: string
      chainIDs: number[]
      website?: string
    }
    response: ResultOrError<
      RelayServiceDBSchema['id'],
      RelayServiceErrorDTO<
        | 'KeyringError'
        | 'ProfileStoreError'
        | 'UserDoesNotExistError'
        | 'AddressNotAllowedError'
        | 'RelayServiceNotReachableError'
        | 'UnhandledRelayServiceSubdomainError'
      >
    >
  }
  [InternalMethods.UPDATE_RELAY_SERVICE]: {
    payload: {
      id: string
      name: string
      apiURL: string
      chainIDs: number[]
      website?: string
    }
    response: ResultOrError<
      void,
      RelayServiceErrorDTO<
        | 'KeyringError'
        | 'ProfileStoreError'
        | 'UserDoesNotExistError'
        | 'AddressNotAllowedError'
        | 'RelayServiceNotFoundError'
        | 'RelayServiceNotReachableError'
        | 'UnhandledRelayServiceSubdomainError'
      >
    >
  }
  [InternalMethods.PROFILE_IMPORTED]: {
    payload: {
      account: string
    }
    response: void
  }
}

export enum InternalMethods {
  HANDLE_APPROVAL = 'handle-approval',
  UNLOCK = 'unlock-keyring',
  VALIDATE_PASSWORD = 'validate-password',
  KEEP_ALIVE = 'keep-alive-keyring',
  GET_STATE = 'get-state',
  SWITCH_PROFILE = 'switch-profile',
  UPLOAD_TO_IPFS = 'upload-to-ipfs',
  REMOVE_AUTHENTICATED_APP = 'remove-authenticated-app',
  SWITCH_NETWORK = 'switch-network',
  ADD_NETWORK = 'add-network',
  SWITCH_LOCALE = 'switch-locale',
  REVEAL_PRIVATE_KEY = 'reveal-private-keys',
  GET_CORE_STATE = 'get-core-state',
  OPEN_NEW_WINDOW_EDIT_VIEW = 'open-new-window-edit-view',
  RECALCULATE_TRANSACTION_DATA = 'recalculate-transaction-data',
  IMPORT_PROFILE = 'import-profile',
  CANCEL_IMPORT_PROFILE = 'cancel-import-profile',
  GET_CONTROLLERS = 'get-controllers',
  GET_UP_BROWSER_EXTENSION_CONTROLLER = 'get-up-browser-extension-controller',
  GET_CONTROLLER_BALANCE = 'get-controller-balance',
  SAVE_CUSTOM_CONTROLLER_NAME = 'save-custom-controller-name',
  FETCH_PROFILE_EVENTS = 'fetch-profile-events',
  MINIMIZE_TRANSACTION = 'minimize-transaction',
  CHANGE_PASSWORD = 'change-password',
  BACK_UP_PROFILES = 'back-up-profiles',
  CHECK_BACKUP_FILE = 'check-backup-file',
  RESTORE_PROFILES = 'restore-profiles',
  CONFIRM_CHANGE_PASSWORD = 'confirm-change-password',
  CREATE_CONTROLLER = 'create-controller',
  CONFIRM_LSP23_ADDRESS = 'confirm-lsp23-address',
  REMOVE_PROFILE = 'remove-profile',
  GET_NETWORKS = 'get-networks',
  READ_APPLICATION_ERRORS = 'read-application-errors',
  ADD_SOMETHING_WENT_WRONG_ERROR = 'add-something-went-wrong-error',
  ADD_RELAY_SERVICE = 'add-relay-service',
  UPDATE_RELAY_SERVICE = 'update-relay-service',
  PROFILE_IMPORTED = 'profile-imported',
}
